import React, { useEffect, useMemo, useState } from "react";
import api from "../../api/axiosInstance";

import { useNavigate } from "react-router-dom";
import { useAuth } from "../../components/navigation/AuthenticationStateManager";

import NavigationTabBar from "../../components/navigation/NavigationTabBar";
import FooterSection from "../../components/navigation/FooterSection";

import PricingCard from "./components/PricingCard";
import ComparisonTable from "./components/ComparisonTable";
import FAQSection from "./components/FAQSection";
import TestimonialCard from "./components/TestimonialCard";
import PricingToggle from "./components/PricingToggle";
import TrustBadges from "./components/TrustBadges";

const PricingPlans = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const [billingCycle, setBillingCycle] = useState("monthly");
  const [plans, setPlans] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [testimonials, setTestimonials] = useState([]);

  /* ---------------- LOAD DATA ---------------- */
  useEffect(() => {
    Promise.all([
      api.get("/api/plans"),
      api.get("/api/faqs?category=pricing"),
      api.get("/api/testimonials?isFeatured=true&isActive=true")
    ])
      .then(([plansRes, faqRes, testimonialRes]) => {
        setPlans(plansRes.data);
        setFaqs(faqRes.data.data);
        setTestimonials(testimonialRes.data);
      })
      .catch(err => console.error("Pricing page load failed", err));
  }, []);

  /* ---------------- CTA HANDLER ---------------- */
  const handleSelectPlan = (plan) => {
    if (plan.planType === "free") {
      navigate("/authentication-portal");
    } else if (plan.planType === "enterprise") {
      window.location.href =
        "mailto:sales@botervue.com?subject=Enterprise Plan Inquiry";
    } else {
      navigate("/authentication-portal");
    }
  };

  /* ---------------- COMPARISON DATA ---------------- */
  const comparisonFeatures = useMemo(() => {
    const allFeatures = new Set();

    plans.forEach(plan =>
      plan.features.forEach(f => allFeatures.add(f.text))
    );

    return Array.from(allFeatures).map(feature => ({
      feature,
      values: Object.fromEntries(
        plans.map(plan => [
          plan.planType,
          plan.features.find(f => f.text === feature)?.included || false
        ])
      )
    }));
  }, [plans]);

  /* ---------------- RENDER ---------------- */
  return (
    <div className="min-h-screen bg-background">
      <NavigationTabBar isAuthenticated={isAuthenticated} />

      <main className="pt-20 pb-20">
        <div className="max-w-7xl mx-auto px-4">

          {/* ---------- HEADER ---------- */}
          <div className="text-center mb-14">
            <h1 className="text-4xl font-bold mb-4">Choose Your Perfect Plan</h1>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Flexible pricing for individuals and teams preparing for interviews
            </p>

            <div className="mt-6">
              <PricingToggle
                billingCycle={billingCycle}
                onToggle={() =>
                  setBillingCycle(billingCycle === "monthly" ? "annual" : "monthly")
                }
              />
            </div>
          </div>

          {/* ---------- PRICING CARDS ---------- */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-20">
            {plans.map(plan => (
              <PricingCard
                key={plan._id}
                plan={plan}
                billingCycle={billingCycle}
                isRecommended={plan.isPopular}
                onSelectPlan={handleSelectPlan}
              />
            ))}
          </div>

          {/* ---------- TRUST ---------- */}
          <div className="mb-20">
            <TrustBadges />
          </div>

          {/* ---------- COMPARISON ---------- */}
          <div className="mb-20">
            <h2 className="text-3xl font-bold text-center mb-10">
              Detailed Feature Comparison
            </h2>

            <ComparisonTable
              plans={plans}
              features={comparisonFeatures}
            />
          </div>

          {/* ---------- TESTIMONIALS ---------- */}
          {testimonials.length > 0 && (
            <div className="mb-20">
              <h2 className="text-3xl font-bold text-center mb-10">
                What Our Students Say
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {testimonials.map(t => (
                  <TestimonialCard key={t._id} testimonial={t} />
                ))}
              </div>
            </div>
          )}

          {/* ---------- FAQ ---------- */}
          {faqs.length > 0 && (
            <div className="mb-20 max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-center mb-10">
                Frequently Asked Questions
              </h2>
              <FAQSection faqs={faqs} />
            </div>
          )}

          {/* ---------- FINAL CTA ---------- */}
          <div className="bg-gradient-to-r from-primary/10 to-accent/10 rounded-xl p-12 text-center">
            <h2 className="text-3xl font-bold mb-4">
              Still Have Questions?
            </h2>
            <p className="text-muted-foreground mb-6">
              Talk to our team to find the best plan for you
            </p>

            <div className="flex justify-center gap-4">
              <button
                onClick={() => (window.location.href = "mailto:support@botervue.com")}
                className="px-6 py-3 bg-primary text-white rounded-md"
              >
                Contact Sales
              </button>
              <button
                onClick={() => navigate("/about-platform")}
                className="px-6 py-3 border rounded-md"
              >
                Learn More
              </button>
            </div>
          </div>

        </div>
      </main>

      <FooterSection />
    </div>
  );
};

export default PricingPlans;
